<template>
    <div>
        <div class="card mb-1">
            <div class="bg-blue-light p-1">
                <TitleButton
                    :showBtn="false"
                    :showAddNew="false"
                    title="Dental Service"
                    :custom-cols="{ leftCol: 4, rightCol: 8 }"
                >
                    <template #extra-btn>
                        <ServiceNab
                            :tabs="tabs"
                            v-model:activeTab="activeTab"
                            v-model:activeComponent="activeComponent"
                            @clickToSubmit="clickToSubmit"
                        />
                        <button
                            class="btn close-btn"
                            @click="onClickToClose"
                        >
                            <i class="fa-solid fa-xmark"></i>
                        </button>
                    </template>
                </TitleButton>
            </div>
            <div class="row" v-if="data && data.bill && data.bill.contact_profile">
                <div class="col-12 col-sm-12 col-md-12 col-lg-12 patient-details">
                    <div class="row">
                        <div class="col-3 col-md-3 pb-1">
                            <div class="name-seg">
                                <b>Name: </b>
                                <b>{{ data.bill.contact_profile.full_name }}({{ data.bill.contact_profile.patient_id }})</b>
                            </div>
                        </div>
                        <div class="col-3 col-md-3 pb-1">
                          <div class="name-seg">
                            <b>Mobile No: </b>
                            <b>{{ data.bill.contact_profile.mobile_no }}</b>
                          </div>
                        </div>
                        <div class="col-3 col-md-3 pb-1">
                            <div class="name-seg">
                                <b>Age: </b>
                                <b>{{ data.bill.contact_profile.birthday ? dateOfBirthConvertToAge(data.bill.contact_profile.birthday).year : '' }} years</b> |
                                <b>Gender: </b>
                                <b class="text-capitalize">{{ data.bill.contact_profile.gender ? data.bill.contact_profile.gender : '' }}</b>
                            </div>
                        </div>
                        <div class="col-3 col-md-3 pb-1">
                            <div class="name-seg">
                                <b>Date: </b>
                                <b>{{ getCurrentDateTime() }}</b>
                            </div>
                        </div>
                        <div class="col-3 col-md-3 pb-1">
                            <div class="name-seg">
                                <b>Consultant: </b>
                                <b>{{ data.bill.service_resource ? data.bill.service_resource.name : '' }}</b>
                            </div>
                        </div>
                        <div class="col-3 col-md-3 pb-1">
                          <div class="name-seg">
                            <b>Dental Service No: </b>
                            <b>{{ data.bill.ipd_register ? data.bill.ipd_register.admission_no : '' }}</b>
                          </div>
                        </div>
                        <div class="col-3 col-md-3 pb-1">
                          <div class="name-seg">
                            <b>Initial Appointment Date: </b>
                            <b>{{ data.bill.ipd_register ? data.bill.ipd_register.admission_date : '' }}</b>
                          </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div id="tooth-numbering"></div>

        <div class="card" v-if="!loader">
            <component
                :is="activeComponent"
                :data="data"
                :currentTab="activeTab"
                v-if="activeTab"
                ref="serviceRef"
                :key="resetKey"
                @reset="resetChildComponent"
            />
        </div>

        <Loader v-if="loader"/>
    </div>

</template>

<script setup>
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, ref, shallowRef, watch} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ServiceNab from '@/components/molecule/company/dental-os/service/ServiceNab.vue';
import Prescription from '@/components/molecule/company/dental-os/service/tabs/Prescription.vue';
import Billing from '@/components/molecule/company/dental-os/service/tabs/Billing.vue';
import Loader from "@/components/atom/LoaderComponent";
import { getCurrentDateTime } from '@/services/utils/global.js'
import { dateOfBirthConvertToAge } from '@/services/utils/global'
import { useStore } from 'vuex';

const { fetchSingleDoctorOrder } = handleDentalOs();

const router = useRouter();
const route = useRoute();
const store = useStore();

const activeTab = ref('prescription');
const activeComponent = shallowRef(Prescription);
const loader = ref(false);
const serviceRef = ref(null);
const resetKey = ref(0);
const showSuccess = inject('showSuccess');
const showError = inject('showError');
const data = ref(null);

const tabs = shallowRef([
    { slug: 'prescription', title: 'Prescription', component: Prescription },
    { slug: 'treatment_plan', title: 'Services', component: Prescription },
    { slug: 'billing', title: 'Billing', component: Billing },
]);

const setCurrentTab = () => {
    let hashPath = route.hash.substring(1) || tabs.value[0].slug;
    activeTab.value = hashPath;

    const tab = tabs.value.find(item => item.slug === hashPath);
    activeComponent.value = tab.component;
}

const clickToSubmit = () => {
    if(! serviceRef.value) return;

    serviceRef.value.submit(false, 'prescription', true);
}

const onClickToClose = () => {
    const backUrl = route.query.back_url ? decodeURIComponent(route.query.back_url) : null;
    if (backUrl) {
        router.push(backUrl);
        return;
    }

    let routeName = 'my-dental-schedule';

    if (route.query.from === 'list') {
        routeName = 'my-dental-schedule-list';
    }

    router.push({
        name: routeName,
        params: route.params,
        query: {
            start: route.query.start,
            end: route.query.end,
            page: route.query.page || 1
        }
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.general_id) query += `&sales_order_general_id=${route.query.general_id}`;
    return query;
}

const fetchInitialData = async () => {
    const serviceId = route.params.serviceId;
    const query = getQuery();

    loader.value = true;

    await fetchSingleDoctorOrder(query, serviceId).then((res) => {
            if (!res.status) return showError(res.message)
            data.value = res.data;
            setOrderMasterId();
        }).finally(() => {
            loader.value = false
        });
}

const setOrderMasterId = () => {
    const orderMasterId = data.value.bill && data.value.bill.ipd_register ? data.value.bill.ipd_register.sales_order_id : null;
    if (! orderMasterId) return;

    router.push({
        name: route.name,
        params: route.params,
        query: {
            ...route.query,
            order_master_id: orderMasterId
        },
        hash: route.hash
    })
}

const resetChildComponent = async () => {
    await fetchInitialData();
    setCurrentTab();
    resetKey.value++;
}

watch(() => serviceRef.value, (newVal) => {
    store.commit('dental/setServiceRef', newVal);
})

watch(() => route.hash, () => {
    setCurrentTab();
})

watch(() => route.query.general_id, () => {
    resetChildComponent();
})

watch(() => store.state.dental.reRenderCount, () => {
    resetChildComponent();
})

onMounted(async () => {
    await fetchInitialData();
    setCurrentTab();
})

</script>

<style scoped>
.close-btn{
    padding: 5px;
    font-size: 29px;
}
.patient-details{
    width: 94%;
    padding: 1% 0;
    margin-left: 30px;
}
</style>
